import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';

/**
 * AuthCallbackPage · `/auth/callback`
 *
 * Recibe magic links de Supabase Auth para 3 flujos:
 *  - `?type=invite` → primer login del invitado → forzar setup de password
 *  - `?type=recovery` → password reset → mandar a /auth/update-password
 *  - `?type=email_change` → confirmación cambio de email → volver a /settings/security
 *
 * Supabase pasa los tokens en el hash (#access_token=…&refresh_token=…&type=…).
 * `detectSessionInUrl: true` (default) hace que `supabase-js` los consuma
 * automáticamente y emita evento `SIGNED_IN`. Nosotros leemos `type` del
 * search param (que la edge function setea en redirectTo) y decidimos
 * a dónde redirigir.
 *
 * Fix anti-loop: el effect corre UNA sola vez al montar usando un ref.
 * `useSearchParams` devuelve un objeto nuevo en cada render → si lo ponemos
 * como dep, el effect corre infinitas veces y dispara replaceState >100/10s.
 */
export function AuthCallbackPage() {
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const [message, setMessage] = useState('Procesando autenticación…');

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');

    void (async () => {
      // Pequeña espera para que detectSessionInUrl procese el hash
      await new Promise((r) => setTimeout(r, 200));
      const { data } = await supabase.auth.getSession();

      if (!data.session) {
        setMessage('No se pudo verificar el link. Probablemente expiró.');
        toast.error('Link inválido o expirado');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
        return;
      }

      // Limpiar el hash del URL antes de redirigir (solo si todavía hay)
      if (window.location.hash) {
        try {
          window.history.replaceState(
            null,
            '',
            window.location.pathname + window.location.search,
          );
        } catch {
          // Si el browser bloqueó por exceso de replaceState, seguimos igual
        }
      }

      switch (type) {
        case 'invite':
          setMessage('Bienvenido a DenDev Core. Definí tu contraseña…');
          toast.success('Invitación aceptada', {
            description: 'Completá tu contraseña para activar la cuenta.',
          });
          setTimeout(
            () => navigate('/auth/update-password?from=invite', { replace: true }),
            500,
          );
          break;

        case 'recovery':
          setMessage('Token válido. Definí tu nueva contraseña…');
          setTimeout(
            () =>
              navigate('/auth/update-password?from=recovery', { replace: true }),
            300,
          );
          break;

        case 'email_change':
          setMessage('Email cambiado correctamente.');
          toast.success('Email actualizado');
          setTimeout(
            () => navigate('/settings/security', { replace: true }),
            500,
          );
          break;

        default:
          // Magic link sin type → dashboard
          setMessage('Sesión iniciada.');
          setTimeout(() => navigate('/dashboard', { replace: true }), 300);
      }
    })();
  }, [navigate]);

  return (
    <main className="bg-background flex min-h-screen items-center justify-center p-6">
      <Card className="border-border bg-card w-full max-w-sm">
        <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
          <Loader2 className="text-primary h-6 w-6 animate-spin" />
          <p className="text-foreground text-body-lg font-semibold">
            DenDev Core
          </p>
          <p className="text-fg-muted text-body">{message}</p>
        </CardContent>
      </Card>
    </main>
  );
}
